/**
 * Définition d'un service métier.
 *
 * Tout passe par ici : l'interface web, le serveur MCP et l'agent de
 * messagerie appellent les mêmes fonctions, donc subissent les mêmes
 * contrôles. Trois implémentations parallèles produiraient trois
 * comportements différents à maintenir — et deux d'entre eux seraient faux.
 */
import type { ExecutionContext, Role } from "./context.js";
import { ActionLevel } from "./levels.js";
import { roleAllows } from "./levels.js";
import {
  DomainError,
  confirmationRequired,
  forbiddenRole,
  untrustedContent,
} from "./errors.js";

/**
 * Registre des actions en attente de confirmation humaine.
 *
 * Adossé à la table `pending_actions`. Tant que personne ne le fournit, toute
 * action à effet externe est refusée : l'absence de mécanisme de confirmation
 * ne doit jamais valoir autorisation.
 */
export interface ConfirmationPort {
  /**
   * Consomme la confirmation désignée et renvoie vrai si elle valait pour
   * cette charge utile — la confirmation porte sur un identifiant d'action,
   * pas sur un « oui » flottant dans une conversation.
   *
   * **Consomme**, et pas seulement « vérifie » : une confirmation ne vaut
   * qu'une fois. Sinon l'appelant qui garde l'identifiant rejoue le même envoi
   * autant de fois qu'il veut, et le patron n'a dit oui qu'une fois.
   *
   * Corollaire assumé : si le handler échoue après coup, la confirmation est
   * brûlée. C'est le bon sens de l'échec fermé — une action à effet externe
   * qui s'est peut-être exécutée à moitié ne se rejoue pas toute seule.
   */
  consume(
    ctx: ExecutionContext,
    actionId: string,
    payloadHash: string,
  ): Promise<boolean>;
}

/** Journal d'audit. Adossé à la table `audit_logs`. */
export interface AuditPort {
  record(entry: {
    ctx: ExecutionContext;
    action: string;
    level: ActionLevel;
    result: "ok" | "denied" | "error";
    payloadHash?: string;
    details?: Record<string, unknown>;
  }): Promise<void>;
}

export interface ServiceDeps {
  confirmations?: ConfirmationPort;
  audit?: AuditPort;
  /** Empreinte de la charge utile. Injectable pour rester testable sans I/O. */
  hash?: (value: unknown) => string;
}

export interface ServiceInput {
  /** Identifiant de la `pending_action` confirmée. Requis au niveau N3. */
  confirmationId?: string;
}

export interface ServiceDefinition<I extends ServiceInput, O> {
  /** Le verbe métier tel qu'il sera exposé au MCP : `envoyer_devis`. */
  name: string;
  level: ActionLevel;
  /**
   * Restriction supplémentaire au niveau d'action.
   *
   * Le niveau dit ce que l'action fait ; ce champ dit qui a le droit de la
   * faire quand ça ne se déduit pas du niveau. Modifier le SIRET ou l'IBAN de
   * l'entreprise est réversible — donc N2 — mais ça engage l'entreprise sur
   * chaque document émis : ce n'est pas au conducteur de travaux d'y toucher.
   *
   * Ne peut qu'élargir la restriction, jamais l'assouplir : un rôle refusé par
   * le niveau reste refusé.
   */
  allowedRoles?: readonly Role[];
  /**
   * Résumé lisible présenté au patron avant confirmation : montant,
   * destinataire, effet. Obligatoire au niveau N3 — une confirmation sans
   * résumé ne vaut rien.
   */
  summarize?: (input: I) => string;
  /**
   * `deps` est transmis pour le seul cas du service qui en appelle un autre :
   * il doit relayer le journal et le registre de confirmations, sinon l'appel
   * interne s'exécute sans audit — et refuse toute action N3 sans qu'on
   * comprenne pourquoi. Les services feuilles l'ignorent.
   */
  handler: (
    ctx: ExecutionContext,
    input: I,
    deps: ServiceDeps,
  ) => Promise<O>;
}

export type Service<I extends ServiceInput, O> = {
  readonly name: string;
  readonly level: ActionLevel;
  /**
   * Le résumé déclaré par le service, reporté sur l'objet appelable.
   *
   * C'est ce qui permet à `describeAction` de produire la question posée au
   * patron sans que l'appelant ait à la réécrire — donc sans qu'un écran
   * puisse afficher autre chose que ce que le service exécutera.
   */
  readonly summarize?: (input: I) => string;
  (ctx: ExecutionContext, input: I, deps?: ServiceDeps): Promise<O>;
};

/**
 * Charge utile soumise à l'empreinte : l'entrée **sans** `confirmationId`.
 *
 * Le retirer n'est pas un détail de propreté, c'est ce qui rend le mécanisme
 * praticable. L'identifiant n'existe pas encore quand on prépare la
 * confirmation, et il existe quand on exécute : le garder ferait diverger les
 * deux empreintes à tous les coups, donc refuserait toutes les actions N3 —
 * y compris les légitimes.
 *
 * Ce qui est signé reste bien la charge métier, celle que le patron a lue.
 */
function businessPayload(input: unknown): unknown {
  if (input === null || typeof input !== "object" || Array.isArray(input)) {
    return input;
  }
  const { confirmationId: _token, ...rest } = input as Record<string, unknown>;
  return rest;
}

function fnv1a(json: string): string {
  // FNV-1a 32 bits : suffisant pour détecter qu'une charge a changé entre la
  // confirmation et l'exécution. Ce n'est pas un usage cryptographique.
  let h = 0x811c9dc5;
  for (let i = 0; i < json.length; i++) {
    h ^= json.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

function defaultHash(value: unknown): string {
  return fnv1a(JSON.stringify(businessPayload(value) ?? null));
}

/**
 * Empreinte d'une charge utile, telle que `defineService` la calculera.
 *
 * Exportée parce que celui qui prépare une confirmation doit produire la même
 * valeur que celui qui l'exécutera. Deux implémentations dériveraient, et la
 * dérive se manifesterait par des refus incompréhensibles.
 */
export function payloadHashOf(input: unknown): string {
  return defaultHash(input);
}

export function defineService<I extends ServiceInput, O>(
  def: ServiceDefinition<I, O>,
): Service<I, O> {
  if (def.level === ActionLevel.ExternalEffect && !def.summarize) {
    throw new Error(
      `Le service « ${def.name} » est de niveau N3 et doit fournir summarize() : ` +
        `on ne demande pas au patron de confirmer une action qu'on ne sait pas lui décrire.`,
    );
  }

  const run = async (
    ctx: ExecutionContext,
    input: I,
    deps: ServiceDeps = {},
  ): Promise<O> => {
    const hash = deps.hash ?? defaultHash;
    const payloadHash = hash(input);

    const deny = async (error: DomainError): Promise<never> => {
      await deps.audit?.record({
        ctx,
        action: def.name,
        level: def.level,
        result: "denied",
        payloadHash,
        details: { code: error.code },
      });
      throw error;
    };

    if (!roleAllows(ctx.role, def.level)) {
      return deny(forbiddenRole(ctx.role, def.level, def.name));
    }

    if (def.allowedRoles && !def.allowedRoles.includes(ctx.role)) {
      return deny(forbiddenRole(ctx.role, def.level, def.name));
    }

    if (def.level === ActionLevel.ExternalEffect) {
      // Règle dure : si la chaîne causale remonte à un contenu externe, la
      // confirmation humaine est obligatoire même en cas de pré-autorisation.
      // Un e-mail fournisseur piégé ne doit pas pouvoir commander du matériel.
      if (ctx.fromUntrustedContent) {
        return deny(untrustedContent(def.name));
      }

      const confirmed =
        input.confirmationId && deps.confirmations
          ? await deps.confirmations.consume(
              ctx,
              input.confirmationId,
              payloadHash,
            )
          : false;

      if (!confirmed) {
        return deny(
          confirmationRequired(def.name, def.summarize?.(input) ?? def.name),
        );
      }
    }

    try {
      const result = await def.handler(ctx, input, deps);
      // Les lectures réussies ne sont pas journalisées : elles noieraient les
      // écritures sous plusieurs ordres de grandeur de bruit, et c'est
      // justement les écritures qu'on doit pouvoir retrouver. Une lecture
      // *refusée* reste journalisée plus haut — une tentative d'accès est un
      // fait à conserver.
      if (def.level !== ActionLevel.Read) {
        await deps.audit?.record({
          ctx,
          action: def.name,
          level: def.level,
          result: "ok",
          payloadHash,
        });
      }
      return result;
    } catch (error) {
      await deps.audit?.record({
        ctx,
        action: def.name,
        level: def.level,
        result: "error",
        payloadHash,
        details: { message: error instanceof Error ? error.message : "inconnue" },
      });
      throw error;
    }
  };

  // `Function.prototype.name` est en lecture seule : Object.assign lèverait.
  // Elle reste configurable, donc defineProperty passe — et on garde un
  // service directement appelable plutôt qu'un objet à déballer.
  return Object.defineProperties(run, {
    name: { value: def.name, configurable: true },
    level: { value: def.level, enumerable: true },
    summarize: { value: def.summarize, enumerable: true },
  }) as Service<I, O>;
}

/** Ce qu'il faut inscrire dans `pending_actions` pour une action donnée. */
export interface ActionDescription {
  action: string;
  level: ActionLevel;
  /** Le texte présenté au patron. Vient du service, jamais de l'appelant. */
  summary: string;
  payloadHash: string;
}

/**
 * Décrit une action telle qu'elle sera exécutée, pour la soumettre à
 * confirmation.
 *
 * Le résumé et l'empreinte sortent tous deux de la définition du service.
 * C'est le point : un écran ne peut pas afficher « relance de 500 € » et
 * exécuter autre chose, parce qu'il n'écrit ni l'un ni l'autre.
 */
export function describeAction<I extends ServiceInput, O>(
  service: Service<I, O>,
  input: I,
): ActionDescription {
  if (service.level !== ActionLevel.ExternalEffect) {
    throw new Error(
      `« ${service.name} » est de niveau ${service.level} : elle s'exécute ` +
        `directement et n'a pas à passer par une confirmation.`,
    );
  }
  return {
    action: service.name,
    level: service.level,
    summary: service.summarize?.(input) ?? service.name,
    payloadHash: payloadHashOf(input),
  };
}
